import { Component, type ErrorInfo, type ReactNode } from 'react'

import { isApiError } from '@/shared/api/errors'
import { ErrorState } from '@/shared/components/data'
import { log } from '@/shared/lib/log'

/**
 * De dónde salió el componente que se rompió.
 *
 * `componentStack` viene como texto multilínea (`\n    in TaskRow (at ...)`).
 * La primera línea es el componente que falló, que es lo único que hace
 * accionable el reporte: "se rompió la app" no se puede investigar, "se rompió
 * `TaskRow`" sí.
 */
function failingComponent(componentStack: string | null | undefined): string | null {
  const first = componentStack?.split('\n').find((line) => line.trim().startsWith('in '))
  return (
    first
      ?.trim()
      .replace(/^in\s+/, '')
      .split(' ')[0] ?? null
  )
}

export interface ErrorBoundaryProps {
  children: ReactNode
  /** Qué parte de la app envuelve. Va al log junto con el componente. */
  boundary: string
  fallback?: (error: unknown, reset: () => void) => ReactNode
}

interface ErrorBoundaryState {
  error: unknown
}

/**
 * Límite de errores de render.
 *
 * El `errorElement` del router atrapa lo que falla al cargar una ruta; esto
 * atrapa lo que explota **mientras se dibuja**, que es el caso que deja la
 * pantalla en blanco sin dejar rastro. Loguea el componente exacto y, si el
 * error vino del API, el `x-request-id`: con eso el error de pantalla y la
 * traza del servidor son el mismo hecho.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null }

  static getDerivedStateFromError(error: unknown): ErrorBoundaryState {
    return { error }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    log.error('render_error', {
      boundary: this.props.boundary,
      component: failingComponent(info.componentStack),
      message: error.message,
      name: error.name,
      request_id: isApiError(error) ? error.requestId : null,
      status: isApiError(error) ? error.status : null,
    })
  }

  private readonly reset = (): void => {
    this.setState({ error: null })
  }

  override render(): ReactNode {
    const { error } = this.state
    if (!error) return this.props.children
    if (this.props.fallback) return this.props.fallback(error, this.reset)

    return (
      <div className="p-6">
        {/*
          Sin `title`: `ErrorState` usa el del catálogo. Esta es una clase —lo
          exige `componentDidCatch`— así que no puede llamar a `useT()`, y
          pasarle el texto en español sería el único lugar de la app donde el
          idioma se decide por la mecánica de React en vez de por quién mira.
        */}
        <ErrorState error={error} onRetry={this.reset} />
      </div>
    )
  }
}
